import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './auth/AuthContext';
import { AppNotistackProvider } from './snackbar/AppNotistackProvider';
import Dashboard from './components/Dashboard';
import PatientList from './components/PatientList';
import PatientRegister from './components/PatientRegister';
import PatientDetail from './components/PatientDetail';
import PatientEdit from './components/PatientEdit';
import Login from './components/Login';
import AuthCallback from './components/AuthCallback';
import LogoutPage from './components/LogoutPage';
import ProtectedRoute from './components/ProtectedRoute';
import MainLayout from './components/MainLayout';
import AdminRoute from './components/AdminRoute';
import StaffList from './components/StaffList';
import StaffNew from './components/StaffNew';
import StaffEdit from './components/StaffEdit';
import Profile from './components/Profile';
import BulletinNew from './components/BulletinNew';
import BulletinDetail from './components/BulletinDetail';
import BulletinEdit from './components/BulletinEdit';
import ManualNew from './components/ManualNew';
import ManualDetail from './components/ManualDetail';
import ManualEdit from './components/ManualEdit';
import ManualGroupsAdmin from './components/ManualGroupsAdmin';
import SettingsPage from './components/SettingsPage';
import BloodTestAnalysis from './components/BloodTestAnalysis';
import { BLOOD_TEST_PATH, BOARD_MANUAL_PATH, PATIENTS_PATH } from './constants/routes';

const theme = createTheme({
  palette: {
    primary: {
      main: '#09b73d',
      // main の輝度からの自動判定されるが指定したいときにcontrastTextを設定する
      contrastText: '#ffffff',
    },
  },
  typography: {
    // h5/h6 の既定（約24px/20px）がやや大きいため、見出しを一段階小さくする
    h5: { fontSize: '1.125rem', fontWeight: 600 },
    h6: { fontSize: '1rem', fontWeight: 600 },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 600 },
  },
  components: {
    // ラベルを枠内中央ではなく上に固定（未入力時も浮かない）
    MuiTextField: {
      defaultProps: {
        size: 'small',
        margin: 'dense',
        InputLabelProps: { shrink: true },
      },
    },
    MuiInputLabel: {
      defaultProps: {
        shrink: true,
      },
      styleOverrides: {
        root: ({ theme }) => ({
          backgroundColor: theme.palette.background.paper,
          paddingLeft: theme.spacing(0.5),
          paddingRight: theme.spacing(0.5),
          zIndex: 1,
        }),
        // size によらずラベル位置を統一（Outlined の入力 padding 左 14px に合わせる）
        outlined: {
          transform: 'translate(14px, -9px) scale(0.75)',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        // ラベルなし（hiddenLabel 等）ではノッチを閉じ、枠線が途切れないようにする
        notchedOutline: ({ theme, ownerState }) => {
          const hasLabel = ownerState.label != null && ownerState.label !== false;
          return {
            legend: hasLabel
              ? {
                  width: 'auto',
                  maxWidth: '100%',
                  fontSize: ownerState.size === 'small' ? '0.75em' : '0.79em',
                }
              : {
                  width: 0,
                  maxWidth: 0,
                  fontSize: 0,
                },
            '& span': hasLabel
              ? {
                  paddingLeft: theme.spacing(0.75),
                  paddingRight: theme.spacing(0.75),
                }
              : {
                  paddingLeft: 0,
                  paddingRight: 0,
                },
          };
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <AppNotistackProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/callback" element={<AuthCallback />} />
              <Route path="/logout" element={<LogoutPage />} />
              <Route element={<ProtectedRoute />}>
                <Route element={<MainLayout />}>
                  <Route path="/" element={<Navigate to={PATIENTS_PATH} replace />} />
                  <Route path={BOARD_MANUAL_PATH} element={<Dashboard />} />
                  <Route path={BLOOD_TEST_PATH} element={<BloodTestAnalysis />} />
                  <Route path={PATIENTS_PATH} element={<PatientList />} />
                  <Route path="/patients/new" element={<PatientRegister />} />
                  <Route path="/patients/:id/edit" element={<PatientEdit />} />
                  <Route path="/patients/:id" element={<PatientDetail />} />
                  <Route path="/profile" element={<Profile />} />
                  <Route path="/bulletin/new" element={<BulletinNew />} />
                  <Route path="/bulletin/:id/edit" element={<BulletinEdit />} />
                  <Route path="/bulletin/:id" element={<BulletinDetail />} />
                  <Route path="/bulletin" element={<Navigate to={BOARD_MANUAL_PATH} replace />} />
                  <Route path="/manual/new" element={<ManualNew />} />
                  <Route path="/manual/groups" element={<ManualGroupsAdmin />} />
                  <Route path="/manual/:id/edit" element={<ManualEdit />} />
                  <Route path="/manual/:id" element={<ManualDetail />} />
                  <Route path="/manual" element={<Navigate to={BOARD_MANUAL_PATH} replace />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route element={<AdminRoute />}>
                    <Route path="/staff" element={<StaffList />} />
                    <Route path="/staff/new" element={<StaffNew />} />
                    <Route path="/staff/:id/edit" element={<StaffEdit />} />
                  </Route>
                </Route>
              </Route>
              <Route path="*" element={<Navigate to={PATIENTS_PATH} replace />} />
            </Routes>
          </AppNotistackProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
